import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import NewPatientModal from "@/components/Patient/NewPatientModal";
import { 
  Users, 
  Search, 
  Plus, 
  Phone, 
  Calendar, 
  Eye,
  UserPlus,
  RefreshCw,
  Mail,
  SortAsc,
  SortDesc,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from "lucide-react";
import { cn } from "@/lib/utils";

// Import API functions
import { getPatients } from '@/lib/api';

// Simplified Patient interface
interface Patient {
  id: string;
  name: string;
  age?: number;
  gender?: string;
  phone?: string;
  email?: string;
  created_at: string;
  updated_at: string;
  doctor_id: string;
  clinic_id: string;
}

export default function PatientManagementPage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => { document.title = "Patients | OneCheckUp"; }, []);

  const [patients, setPatients] = useState<Patient[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [genderFilter, setGenderFilter] = useState("");
  const [sortField, setSortField] = useState<'name' | 'created_at' | 'age'>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [loading, setLoading] = useState(false);
  const [showAddPatient, setShowAddPatient] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [totalItems, setTotalItems] = useState(0);

  // Fetch patients from API
  const fetchPatients = async () => {
    // clinic_id is required; doctor_id is optional (receptionists don't have one)
    if (!currentUser?.clinic_id) {
      console.warn('Missing clinic_id');
      return;
    }

    setLoading(true);
    try {
      const result: { patients: Patient[]; total: number } = await getPatients({
        clinic_id: currentUser.clinic_id,
        search: searchTerm,
        gender: genderFilter,
        sortField,
        sortOrder,
        page: currentPage,
        pageSize: itemsPerPage
      });
      setPatients(result.patients);
      setTotalItems(result.total);
    } catch (error) {
      console.error('Failed to fetch patients:', error);
      setPatients([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPatients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, genderFilter, sortField, sortOrder, currentPage, itemsPerPage]);

  // Handle navigation state for focus search
  useEffect(() => {
    const handleNavigationContext = () => {
      const state = location.state;
      
      // Handle focus search from dashboard
      if (state?.focusSearch) {
        // Focus the search input when component mounts
        setTimeout(() => {
          const searchInput = document.querySelector('input[placeholder*="Search"]') as HTMLInputElement;
          if (searchInput) {
            searchInput.focus();
            searchInput.select(); // Select any existing text for easy replacement
          }
        }, 100);
      }
    };

    handleNavigationContext();
  }, [location.state]);

  // Pagination calculations
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const startItem = totalItems === 0 ? 0 : startIndex + 1;
  const endItem = Math.min(startIndex + itemsPerPage, totalItems);

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page
  };

  // Reset pagination when search/filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, genderFilter]);

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const handleCreatePrescription = (patient: Patient) => {
    navigate(`/patients/${patient.id}?openPrescription=true`);
    setShowAddPatient(false);
  };

  const handlePatientAdded = (newPatient: any) => {
    setPatients(prev => [...prev, newPatient]);
    setShowAddPatient(false);
  };

  // Simple patient card component
  const renderPatientCard = (patient: Patient) => (
    <Card 
      key={patient.id} 
      className="hover:shadow-md transition-shadow cursor-pointer"
      onClick={() => navigate(`/patients/${patient.id}`)}
    >
      <CardContent className="p-4">
        <div className="flex items-start space-x-3">
        {/* Avatar */}
          <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-semibold">
            {patient.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
        </div>

        {/* Patient Info */}
        <div className="flex-1 min-w-0">
            <h3 className="font-medium text-foreground truncate">
                {patient.name}
              </h3>
            
            <div className="text-sm text-muted-foreground mt-1">
              {patient.age && patient.gender ? `${patient.age} years • ${patient.gender}` : 
               patient.age ? `${patient.age} years` : patient.gender || ''}
          </div>

          {/* Contact Info */}
            <div className="mt-2 space-y-1">
            {patient.phone && (
              <div className="flex items-center text-sm text-muted-foreground">
                  <Phone className="w-3 h-3 mr-2" />
                {patient.phone}
              </div>
            )}
            {patient.email && (
              <div className="flex items-center text-sm text-muted-foreground">
                  <Mail className="w-3 h-3 mr-2" />
                <span className="truncate">{patient.email}</span>
              </div>
            )}
          </div>

            {/* Created date */}
            <div className="mt-2 text-xs text-muted-foreground">
              Added: {new Date(patient.created_at).toLocaleDateString()}
            </div>
          </div>

          {/* Actions - different for doctors vs receptionists */}
          <div className="flex flex-col gap-1">
            <Button
                  variant="outline" 
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/patients/${patient.id}`);
              }}
            >
              <Eye className="w-3 h-3 mr-1" />
              View
            </Button>
            
            {/* Staff/Receptionist actions: Book Appointment */}
            {currentUser?.role === 'staff' && (
              <Button
                variant="default"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/frontdesk/appointments?patientId=${patient.id}`);
                }}
              >
                <Calendar className="w-3 h-3 mr-1" />
                Book
              </Button>
            )}
            
            {/* Doctor actions: Prescribe */}
            {currentUser?.role !== 'staff' && (
              <Button
                variant="default"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCreatePrescription(patient);
                }}
              >
                Prescribe
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="prescription-form-container">
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-border pb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-primary/10 rounded-xl">
              <Users className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Patient Management</h1>
              <p className="text-sm text-muted-foreground">
                Manage your patients • {patients.length} total patients
              </p>
            </div>
          </div>
          
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={fetchPatients}
              disabled={loading}
            >
              <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />
              Refresh
            </Button>
            
            <Button onClick={() => setShowAddPatient(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Patient
            </Button>
          </div>
          </div>
        </div>

      {/* Search and Filter Controls */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
          <div className="flex flex-col sm:flex-row gap-4 flex-1">
          {/* Search */}
            <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
                id="patient-search"
                name="patient-search"
                type="text"
              placeholder="Search patients by name, phone, or MRN id..."
              value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
          </div>
          
            {/* Gender Filter */}
            <select
              id="patient-gender-filter"
              name="patient-gender-filter"
              value={genderFilter}
              onChange={(e) => setGenderFilter(e.target.value)}
              className="min-w-[120px] px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="all">All Genders</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>

            {/* Items per page */}
            <select
              id="patient-items-per-page"
              name="patient-items-per-page"
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              className="min-w-[100px] px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="12">12 per page</option>
              <option value="24">24 per page</option>
              <option value="48">48 per page</option>
              <option value="96">96 per page</option>
            </select>
        </div>

          {/* Sort Controls */}
          <div className="flex gap-2">
            <select
              id="patient-sort-field"
              name="patient-sort-field"
              value={sortField}
              onChange={(e) => setSortField(e.target.value as 'name' | 'created_at' | 'age')}
              className="px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="created_at">Sort by Date</option>
              <option value="name">Sort by Name</option>
              <option value="age">Sort by Age</option>
            </select>
            
                <Button
              variant="outline"
              onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
              className="flex items-center gap-2"
            >
              {sortOrder === 'desc' ? (
                <SortDesc className="w-4 h-4" />
              ) : (
                <SortAsc className="w-4 h-4" />
              )}
              {sortOrder === 'desc' ? 'Newest First' : 'Oldest First'}
                </Button>
                      </div>
                      </div>
                    </div>

      {/* Patient List */}
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">
            Patients ({totalItems})
          </h2>
          {totalItems > 0 && (
            <div className="text-sm text-muted-foreground">
              Showing {startItem}-{endItem} of {totalItems} results
                  </div>
                )}
              </div>
          
      {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="text-muted-foreground mt-4">Loading patients...</p>
        </div>
      ) : patients.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">
                {searchTerm ? 'No patients found' : 'No patients yet'}
            </h3>
            <p className="text-muted-foreground mb-6">
                {searchTerm 
                  ? 'Try adjusting your search terms' 
                  : 'Add your first patient to get started'
              }
            </p>
              {!searchTerm && (
                <Button onClick={() => setShowAddPatient(true)}>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add First Patient
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {patients.map(renderPatientCard)}
          </div>
        )}
          
          {/* Pagination */}
          {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-border">
              <div className="text-sm text-muted-foreground">
              Showing {startItem}-{endItem} of {totalItems} patients
              </div>
            
            <div className="flex items-center gap-2">
              {/* First Page */}
                <Button
                  variant="outline"
                size="sm"
                onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                className="p-2"
              >
                <ChevronsLeft className="w-4 h-4" />
              </Button>
              
              {/* Previous Page */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
              
              {/* Page Numbers */}
              <div className="flex items-center gap-1">
                {getPageNumbers().map((pageNum, index) => (
                  <React.Fragment key={index}>
                    {pageNum === '...' ? (
                      <span className="px-2 py-1 text-sm text-muted-foreground">...</span>
                    ) : (
                      <Button
                        variant={currentPage === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(pageNum as number)}
                        className="w-8 h-8 p-0"
                      >
                        {pageNum}
                      </Button>
                    )}
                  </React.Fragment>
                ))}
              </div>
              
              {/* Next Page */}
                <Button
                  variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                className="p-2"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              
              {/* Last Page */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className="p-2"
              >
                <ChevronsRight className="w-4 h-4" />
              </Button>
              
              {/* Quick page navigation */}
              <div className="flex items-center gap-2 text-sm ml-4">
                <span className="text-muted-foreground">Go to:</span>
                <Input
                  id="patient-page-input"
                  name="patient-page-input"
                  type="number"
                  min="1"
                  max={totalPages}
                  value={currentPage}
                  onChange={(e) => {
                    const page = parseInt(e.target.value);
                    if (page >= 1 && page <= totalPages) {
                      handlePageChange(page);
                    }
                  }}
                  className="w-16 h-8 px-2 py-1 text-center text-sm"
                />
                <span className="text-muted-foreground">of {totalPages}</span>
              </div>
              </div>
            </div>
          )}
      </div>

      {/* Add Patient Modal */}
      {showAddPatient && (
        <NewPatientModal
          isOpen={showAddPatient}
          onClose={() => setShowAddPatient(false)}
          onPatientCreated={() => {
            fetchPatients();
            setShowAddPatient(false);
          }}
        />
      )}
      </div>
    </div>
  );
}            